import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const CARDS = [
  {
    id: "progetti",
    to: "/progetti",
    titolo: "Progetti",
    descrizione:
      "Le iniziative strategiche promosse da Confcommercio Roma insieme alla Camera di Commercio, agli enti istituzionali e ai partner del territorio: sviluppo del retail, valorizzazione dei distretti, sostenibilità, welfare, formazione imprenditoriale e transizione digitale.",
    cover: "/local-assets/progetti-cover.webp",
    cta: "Esplora i progetti",
  },
  {
    id: "osservatorio",
    to: "/osservatorio-economico",
    titolo: "L'Osservatorio Economico",
    descrizione:
      "Ricerche, analisi e rapporti sui comportamenti d'acquisto dei consumatori, sulla struttura del terziario romano e sull'andamento delle imprese di commercio, turismo e servizi. Un patrimonio di dati aggiornato per interpretare l'economia della Capitale.",
    cover: "/local-assets/osservatorio-cover.webp",
    cta: "Vai all'Osservatorio",
  },
  {
    id: "bussole",
    to: "/le-bussole",
    titolo: "Le Bussole",
    descrizione:
      "La collana di manuali pratici e operativi curata da Confcommercio insieme alle Associazioni di categoria: strumenti concreti su gestione, marketing, vendite e organizzazione, pensati per le imprese del commercio, turismo e servizi di ogni dimensione.",
    cover: "https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800&q=70&auto=format",
    cta: "Scopri Le Bussole",
  },
];

export default function RicercaProgettiPage() {
  useEffect(() => {
    document.title = "Ricerca e Progetti | Confcommercio Roma";
    window.scrollTo(0, 0);
  }, []);

  return (
    <main id="main-content" data-testid="ricerca-progetti-page" className="bg-white">
      {/* ── HERO con immagine di sfondo ── */}
      <section
        className="relative h-[320px] md:h-[380px] overflow-hidden"
        data-testid="ricerca-progetti-hero"
      >
        <img
          src="/local-assets/ricerca-progetti-hero.webp"
          alt="Analisi economica e progetti strategici"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(4, 30, 66, 0.82)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative h-full flex flex-col justify-end pb-12">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">
            Analisi, studi e iniziative
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mt-3 font-['Encode_Sans_Semi_Condensed'] tracking-tight">
            Ricerca e Progetti
          </h1>
          <p className="text-lg text-white/80 mt-4 max-w-3xl">
            L'attività di ricerca economica e i progetti strategici di Confcommercio Roma a supporto dello sviluppo del terziario capitolino.
          </p>
        </div>
      </section>

      {/* ── Introduzione + Card ── */}
      <section
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24"
        data-testid="ricerca-progetti-cards"
      >
        <div className="mb-14 md:mb-16">
          <span
            className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']"
            data-testid="ricerca-progetti-intro-overline"
          >
            Tre aree, un unico obiettivo
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-[var(--confcom-text-primary)] mt-3 font-['Encode_Sans_Semi_Condensed'] tracking-tight">
            Conoscere il territorio, progettare il futuro
          </h2>
          <p
            className="text-[var(--confcom-text-secondary)] leading-relaxed mt-4"
            data-testid="ricerca-progetti-intro-text"
          >
            Confcommercio Roma affianca alle attività di rappresentanza un solido lavoro di analisi e sperimentazione. Da un lato, l'<strong>Osservatorio Economico</strong> monitora consumi, imprese e tendenze del terziario capitolino producendo ricerche periodiche a disposizione di associati e istituzioni. Dall'altro, i <strong>Progetti</strong> — sviluppati con la Camera di Commercio di Roma e con partner strategici — traducono queste evidenze in iniziative concrete a beneficio delle imprese e della città. Completa l'offerta <strong>Le Bussole</strong>, la collana di manuali pratici per la gestione quotidiana dell'impresa.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {CARDS.map(({ id, to, titolo, descrizione, cover, cta }) => (
            <Link
              key={id}
              to={to}
              data-testid={`rp-card-${id}`}
              className="group rounded-2xl border border-[var(--confcom-border)] bg-white hover:shadow-xl hover:border-[var(--confcom-blue)]/40 transition-all overflow-hidden flex flex-col"
            >
              <div className="relative h-56 overflow-hidden bg-[#EBF5FF]">
                <img
                  src={cover}
                  alt={titolo}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="p-8 flex-1 flex flex-col">
                <h2 className="text-2xl font-bold text-[var(--confcom-blue)] font-['Encode_Sans_Semi_Condensed'] mb-3 group-hover:underline">
                  {titolo}
                </h2>
                <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-6 flex-1">
                  {descrizione}
                </p>
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--confcom-blue)] group-hover:gap-3 transition-all">
                  {cta} <ArrowRight size={16} aria-hidden="true" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
}
